import moment from 'moment';
import { BaseSyntheticEvent, HTMLInputTypeAttribute } from 'react';
import { FieldError, FieldValues, Path, UseFormReturn } from 'react-hook-form';

import { FULL_WIDTH_TO_ASCII_OFFSET, HK_LIST, ZK_LIST } from './const';

export type LiteralValueMap = {
  preferredLang: 'JP' | 'CN' | 'HK' | 'TW' | 'EN';
  gender: 'male' | 'female' | 'other';
};

export type RawField = {
  name: string;
  label: string;
  valueType: string;
  maxLength?: number;
  required?: boolean;
  pattern?: string;
  type?: HTMLInputTypeAttribute;
  tabIndex?: number;
  validator?: (name: string, methods: UseFormReturn<any>) => void;
};

export type ValueTypeResolver<T extends RawField> = T['valueType'] extends `literal:${infer L}`
  ? LiteralValueMap[L & keyof LiteralValueMap]
  : T['valueType'] extends 'number'
  ? number
  : T['valueType'] extends 'boolean'
  ? boolean
  : T['valueType'] extends 'date'
  ? Date | string
  : string;

export const InputFields = {
  prepaidCode: {
    name: 'prepaidCode',
    label: 'チケット番号',
    maxLength: 255,
    valueType: 'string',
    required: false,
    pattern: undefined,
    type: 'text',
    tabIndex: undefined,
    validator: undefined,
  },
  hospitalPatientId: {
    name: 'hospitalPatientId',
    label: '院内ID',
    maxLength: 255,
    valueType: 'string',
    required: false,
    pattern: '\\S+( *$)?',
    type: 'text',
    tabIndex: undefined,
    validator: normalizeToHalfWidth,
  },
  patientId: {
    name: 'patientId',
    label: 'Brain-ID',
    maxLength: 50,
    valueType: 'string',
    required: false,
    pattern: undefined,
    type: 'text',
    tabIndex: undefined,
    validator: undefined,
  },
  patientName: {
    name: 'patientName',
    label: '氏名',
    maxLength: 255,
    valueType: 'string',
    required: false,
    pattern: undefined,
    type: 'text',
    tabIndex: undefined,
    validator: undefined,
  },
  gender: {
    name: 'gender',
    label: '性別',
    valueType: 'literal:gender',
    required: false,
    pattern: undefined,
    type: 'text',
    tabIndex: undefined,
    validator: undefined,
  },
  birthdate: {
    name: 'birthdate',
    label: '生年月日(YYYY-MM-DD)',
    maxLength: 50,
    valueType: 'string',
    required: false,
    pattern: undefined,
    type: 'text',
    tabIndex: undefined,
    validator: normalizeAndValidateDate,
  },
  email: {
    name: 'email',
    label: 'メールアドレス',
    maxLength: 255,
    valueType: 'string',
    required: false,
    pattern: '^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$',
    type: 'email',
    tabIndex: undefined,
    validator: undefined,
  },
  comment: {
    name: 'comment',
    label: '医師のコメント',
    maxLength: 5000,
    valueType: 'string',
    required: false,
    pattern: undefined,
    type: 'text',
    tabIndex: undefined,
    validator: undefined,
  },
  preferredLang: {
    name: 'preferredLang',
    label: 'レポート言語',
    valueType: 'literal:preferredLang',
    required: false,
    pattern: undefined,
    type: 'text',
    tabIndex: undefined,
    validator: undefined,
  },
  password: {
    name: 'password',
    label: 'パスワード',
    maxLength: 255,
    valueType: 'string',
    required: false,
    pattern: undefined,
    type: 'password',
    tabIndex: undefined,
    validator: undefined,
  },
  mfaCode: {
    name: 'mfaCode',
    label: 'セキュリティコード',
    maxLength: 255,
    valueType: 'string',
    required: false,
    pattern: undefined,
    type: 'password',
    tabIndex: undefined,
    validator: undefined,
  },
} as const satisfies Record<string, RawField>;

export const genderTable = {
  male: '男性',
  female: '女性',
  other: '他/不明',
};

export type FormValuesFromFields<T extends Record<string, RawField>> = {
  [K in keyof T as T[K]['name']]: ValueTypeResolver<T[K]>;
};

export function getAllErrors<T extends FieldValues>(fields: Array<Path<T>>, methods: UseFormReturn<T>): Partial<Record<Path<T>, FieldError>> {
  return fields.reduce((acc, field) => {
    const state = methods.getFieldState(field);
    if (state.error) {
      acc[field] = state.error;
    }
    return acc;
  }, {} as Partial<Record<Path<T>, FieldError>>);
}

export function withValidation<T extends FieldValues, F extends Record<string, RawField>>(
  formFields: F,
  methods: UseFormReturn<T>,
  onValid: (data?: any, event?: BaseSyntheticEvent) => void,
) {
  return (data?: any, event?: BaseSyntheticEvent) => {
    // Run field-specific validators (if defined)
    (Object.keys(formFields) as Array<keyof F>).forEach((key) => {
      const validator = formFields[key].validator;
      if (validator) {
        validator(formFields[key].name, methods);
      }
    });

    // Check if any errors remain after validation
    const allFields = Object.keys(methods.getValues()) as Array<Path<T>>;
    const errors = getAllErrors<T>(allFields, methods);

    if (Object.keys(errors).length > 0) return;

    // Proceed with submission
    onValid(data, event);
  };
}

// region Validation
// // // // // // // //
// hospitalPatientId //
// // // // // // // //
export function normalizeToHalfWidth<T extends Record<string, any>>(name: Path<T>, { getValues, setValue, clearErrors }: UseFormReturn<T>) {
  const raw = getValues()[name];
  const ansiNormalized = convertAsciiToHalfWidth(raw);
  const halfWidthNormalized = convertZKtoHK(ansiNormalized).trim();

  // Here can be some validation/check
  // setError can be used

  setValue(name, halfWidthNormalized as T[typeof name]);
  clearErrors(name);
}
// Full-width ASCII → Half-width
function convertAsciiToHalfWidth(input: string): string {
  return input.replace(/[\uFF01-\uFF5E]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - FULL_WIDTH_TO_ASCII_OFFSET)).replace(/\u3000/g, ' '); // full-width space
}
// Full-width Katakana (ZK) → Half-width Katakana (HK)
function convertZKtoHK(input: string): string {
  const kanaMap = new Map<string, string>();
  ZK_LIST.forEach((char, idx) => kanaMap.set(char, HK_LIST[idx]));

  return input
    .split('')
    .map((ch) => kanaMap.get(ch) ?? ch)
    .join('');
}

// // // // //
// birthdate//
// // // // //
const softDateRegex = /^(1\d{3}|2\d{3}|3000)-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
const normalizeDate = (raw: string): string => {
  let cleaned = raw
    .replace(/[０-９]/g, (d) => String.fromCharCode(d.charCodeAt(0) - FULL_WIDTH_TO_ASCII_OFFSET)) // convert full-width digits to ASCII
    .replace(/[−ー―–—]/g, '-') // unify various dash characters to hyphen (-)
    .replace(/[年月日]/g, '-') // replace Japanese date symbols with hyphen
    .replace(/[^\d\-]/g, '') // remove all characters except digits and hyphen
    .replace(/\/+/g, '-') // convert slashes to hyphens
    .replace(/--+/g, '-') // collapse multiple hyphens
    .trim();

  // If the input is 8 digits only (e.g., 19920818), format it as YYYY-MM-DD
  const onlyDigits = cleaned.replace(/\D/g, '');
  if (onlyDigits.length === 8) {
    const y = onlyDigits.slice(0, 4);
    const m = onlyDigits.slice(4, 6);
    const d = onlyDigits.slice(6, 8);
    return `${y}-${m}-${d}`;
  }

  return cleaned; // otherwise, return the normalized value as-is
};
export function normalizeAndValidateDate<T extends Record<string, any>>(
  name: Path<T>,
  { getValues, setError, setValue, clearErrors }: UseFormReturn<T>,
) {
  const raw = getValues()[name];
  const normalized = normalizeDate(raw);
  const isSoftValid = softDateRegex.test(normalized);
  const formatted = moment(normalized, 'YYYY-MM-DD', true); // strict

  if (!isSoftValid || !formatted.isValid()) {
    setError(name, {
      type: 'manual',
      message: '日付が正しくありません。',
    });
    return;
  }

  setValue(name, formatted.format('YYYY-MM-DD') as T[typeof name]);
  clearErrors(name);
}
